import { Button, color } from "@funded-here-interface/common";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useStyle } from "../../styles/InvestorDocUpload";
import { investordata } from "./Data";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import { Persona } from "@funded-here-interface/common/src/constant/enum";
import FileUploadComponent from "../../components/FileUpload/FileUpload";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";

// Custom hooks and utilities
import { useInvestorDraftSync } from "./hooks/useInvestorDraftSync";
import { useInvestorOrgSubmission } from "./hooks/useInvestorOrgSubmission";
import {
  extractFilePaths,
  getExistingFilesForInvestorType,
  validateRequiredInstitutionalInvestorDocuments,
} from "./utils/documentHelpers";

const InvestorDocsUpload: FC = () => {
  const { classes } = useStyle();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const navigate = useNavigate();

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Redux selectors - Auth
  const { userId, kycStatus, orgId, token, type, entityType } = useSelector(
    (state: RootState) => state.auth
  );

  const isIspv = type === Persona.ISPV;

  // Redux selectors - Documents
  const {
    proof: proofOfAddress,
    share: shareholdersDirectorsRegisters,
    financialStatement,
    business: businessProfile,
    investmentMandate,
    fundManagerLicense,
    certificateOfIncorporation,
    companyConstitution,
    crsSelfCertificationForm,
    proofOfBankAccountDetails,
  } = useSelector((state: RootState) => state.bordoc);

  // Redux selectors - Business state
  const bizState = useSelector((state: RootState) => state.biz);

  // Redux selectors - Representative state
  const repState = useSelector((state: RootState) => state.rep);

  // Redux selectors - Bank state
  const bankState = useSelector((state: RootState) => state.bank);

  // Custom hooks
  const { syncInvestorDraftToRedux } = useInvestorDraftSync();
  const { submitInvestorOrganization } = useInvestorOrgSubmission({
    setIsLoading,
    onError: (error) => toast.error(error.message),
  });

  const hasFetched = useRef(false);

  /**
   * Load draft data from backend and sync to Redux
   */
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!hasFetched.current && userId) {
        try {
          const data = await getOrgDraftMutation.mutateAsync({
            userId: parseInt(userId),
            token,
          });

          if (data && isMounted) {
            syncInvestorDraftToRedux(data);
            hasFetched.current = true;
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        businessProfile,
        shareHoldersDirectorsRegisters: shareholdersDirectorsRegisters,
        financialStatement,
        proofOfAddress,
        investmentMandate,
        fundManagerLicense,
        certificateOfIncorporation,
        companyConstitution,
        crsSelCertificationForm: crsSelfCertificationForm,
        proofOfBankAccountDetails,
      },
      {
        onSuccess: () => {
          if (showToast) {
            toast.success("Draft Saved");
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Filter documents based on persona (remove CRS form for ISPV)
  const documents = [...investordata].filter((doc) =>
    type === Persona.ISPV
      ? doc.id !== FileEnum.CRS_SELF_CERTIFICATION_FORM
      : true
  );

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    saveDraft();
    navigate("/bank-details");
  };

  /**
   * Handle next - validate documents and submit organization
   */
  const handleNext = async () => {
    saveDraft(false);

    // Clear previous errors
    setErrors({});

    // Define validation rules for required documents
    const requiredDocuments = [
      {
        file: certificateOfIncorporation,
        errorKey: "certificateOfIncorporation",
        displayName: "Certificate of Incorporation",
      },
      {
        file: businessProfile,
        errorKey: "businessProfileFile",
        displayName: "Company/Business Profile",
      },
      {
        file: companyConstitution,
        errorKey: "companyConstitution",
        displayName: "Company Constitution",
      },
      {
        file: proofOfAddress,
        errorKey: "proofOfAddress",
        displayName: "Proof of Residential Address",
      },
      {
        file: financialStatement,
        errorKey: "financialStatement",
        displayName: "Financial Documents",
      },
      {
        file: proofOfBankAccountDetails,
        errorKey: "proofOfBankAccountDetails",
        displayName: "Proof of Bank Account Details",
      },
      // Conditionally include CRS form (not required for ISPV)
      ...(type !== Persona.ISPV
        ? [
            {
              file: crsSelfCertificationForm,
              errorKey: "crsSelfCertificationForm",
              displayName: "CRS Self-Certification Form",
            },
          ]
        : []),
    ];

    // Validate all required documents
    const newErrors: { [key: string]: string } = {};
    const missingDocuments: string[] = [];

    requiredDocuments.forEach(({ file, errorKey, displayName }) => {
      if (!file || file.length === 0) {
        newErrors[errorKey] = `${displayName} is required`;
        missingDocuments.push(displayName);
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(`Please upload ${missingDocuments.join(", ")}`);
      return;
    }

    // Build documents upload object with file paths
    const documentsUpload: DocumentsUpload = {
      proofOfAddress: extractFilePaths(proofOfAddress),
      businessProfile: extractFilePaths(businessProfile),
      shareHoldersDirectorsRegisters: extractFilePaths(
        shareholdersDirectorsRegisters
      ),
      financialStatement: extractFilePaths(financialStatement),
      investmentMandate:
        investmentMandate.length > 0
          ? extractFilePaths(investmentMandate)
          : undefined,
      fundManagerLicense:
        fundManagerLicense.length > 0
          ? extractFilePaths(fundManagerLicense)
          : undefined,
      certificateOfIncorporation: extractFilePaths(certificateOfIncorporation),
      companyConstitution: extractFilePaths(companyConstitution),
      crsSelCertificationForm: extractFilePaths(crsSelfCertificationForm),
      proofOfBankAccountDetails: extractFilePaths(proofOfBankAccountDetails),
    };

    // Submit organization (create or update based on KYC status)
    submitInvestorOrganization({
      kycStatus: kycStatus || null,
      orgId: orgId?.toString() || null,
      token,
      onBoardingUserType: type as string,
      entityType: entityType as string,
      bizState,
      repState,
      bankState,
      documentsUpload,
    });
  };

  return (
    <OnboardingLayout
      showSteps={true}
      step={4}
      content={
        <div>
          <p className={classes.headinfo}>
            All uploads must be in PDF format. The file size should not exceed
            10MB each.
            <br />
            We may request for additional supporting documents beyond those
            listed here as part of our onboarding and verification process.
          </p>
          <div className={classes.stepContent} style={{ background: "#fff" }}>
            {documents.map((item) => (
              <FileUploadComponent
                key={item.number}
                number={item.number}
                fileId={item.id}
                isIspv={isIspv}
                FileName={item.heading}
                existingFiles={getExistingFilesForInvestorType(item.id, {
                  businessProfile,
                  companyConstitution,
                  certificateOfIncorporation,
                  crsSelfCertificationForm,
                  proofOfBankAccountDetails,
                  financialStatement,
                  proofOfAddress,
                  shareholdersDirectorsRegisters,
                  investmentMandate,
                  fundManagerLicense,
                })}
                errors={errors}
                onClearError={(errorKey) =>
                  setErrors((prev) => ({ ...prev, [errorKey]: "" }))
                }
              />
            ))}
            <div className={classes.buttonDiv2}>
              <div className={classes.buttonWrapper}>
                <Button
                  onClick={handleBack}
                  backgroundColor={color.WHITE}
                  textColor={color.FHGREEN}
                  children="Back"
                  border="1px solid #338F86"
                  width="210px"
                />
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  disabled={isLoading}
                  onClick={handleNext}
                  textColor={color.WHITE}
                  backgroundColor={color.FHGREEN}
                  children="Submit"
                  width="210px"
                />
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  onClick={saveDraft}
                  textColor={color.WHITE}
                  backgroundColor={color.LOANTEXT}
                  children="Save Draft"
                  width="100%"
                />
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default InvestorDocsUpload;
